import React from 'react';

/* Dibujos isométricos del mundo Qyntix: los mismos bloques azules y papeles
   del hero, en SVG. Cada pieza es una lista de vóxeles [x, y, z, acento?] y de
   papeles { x, y, z, w, h } apoyados en horizontal.
   Solo se dibujan las caras que se ven (sin rejilla interior) y cada pieza
   lleva su sombra en el suelo. */

const C30 = Math.cos(Math.PI / 6);
// Los colores son variables CSS (styles.css): la paleta se cambia allí.
const TONES = {
  blue: { top: 'var(--cube-top)', left: 'var(--cube)', right: 'var(--cube-side)', accentTop: 'var(--accent-top)', accentLeft: 'var(--accent-left)', accentRight: 'var(--accent-right)' },
  // para fondos de color: bloques claros con acento oscuro
  light: { top: 'var(--accent-top)', left: 'var(--accent-left)', right: 'var(--accent-right)', accentTop: 'var(--cube-top)', accentLeft: 'var(--ink-cube)', accentRight: 'var(--ink-cube-side)' },
};

const project = (x, y, z, s) => [(x - y) * C30 * s, (x + y) * 0.5 * s - z * s];
const pts = (arr) => arr.map(([a, b]) => `${a.toFixed(2)},${b.toFixed(2)}`).join(' ');

export function IsoArt({ voxels = [], papers = [], frame, s = 16, tone = 'blue', shadow = true, seams = false, title, className = 'iso' }) {
  const T = TONES[tone];
  // con costuras se dibujan todas las caras, para poder contar los bloques
  const occupied = new Set(seams ? [] : voxels.map(([x, y, z]) => `${x},${y},${z}`));
  const has = (x, y, z) => occupied.has(`${x},${y},${z}`);

  const items = [
    ...voxels.map(([x, y, z, accent]) => ({ kind: 'cube', x, y, z, accent, key: x + y + z })),
    ...papers.map((p) => ({ kind: 'paper', ...p, key: p.x + p.y + p.z + (p.w + p.h) / 2 - 0.5 })),
  ].sort((a, b) => a.key - b.key || a.z - b.z);

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const grow = ([a, b]) => { minX = Math.min(minX, a); maxX = Math.max(maxX, a); minY = Math.min(minY, b); maxY = Math.max(maxY, b); };
  const P = (a, b, c) => { const p = project(a, b, c, s); grow(p); return p; };

  // sombra en el suelo: el rombo que cubre la huella de la pieza
  let ground = null;
  if (shadow) {
    const xs = [...voxels.map((v) => [v[0], v[0] + 1]), ...papers.map((p) => [p.x, p.x + p.w])].flat();
    const ys = [...voxels.map((v) => [v[1], v[1] + 1]), ...papers.map((p) => [p.y, p.y + p.h])].flat();
    if (xs.length) {
      const x0 = Math.min(...xs) - 0.35, x1 = Math.max(...xs) + 0.6;
      const y0 = Math.min(...ys) - 0.35, y1 = Math.max(...ys) + 0.6;
      ground = <polygon points={pts([P(x0, y0, 0), P(x1, y0, 0), P(x1, y1, 0), P(x0, y1, 0)])} fill="var(--navy)" opacity={tone === 'light' ? 0.18 : 0.08} />;
    }
  }

  const shapes = items.map((it, i) => {
    if (it.kind === 'cube') {
      const { x, y, z } = it;
      const c = it.accent
        ? { top: T.accentTop, left: T.accentLeft, right: T.accentRight }
        : { top: T.top, left: T.left, right: T.right };
      const faces = [];
      if (!has(x, y + 1, z)) faces.push(['left', [P(x, y + 1, z), P(x + 1, y + 1, z), P(x + 1, y + 1, z + 1), P(x, y + 1, z + 1)]]);
      if (!has(x + 1, y, z)) faces.push(['right', [P(x + 1, y, z), P(x + 1, y + 1, z), P(x + 1, y + 1, z + 1), P(x + 1, y, z + 1)]]);
      if (!has(x, y, z + 1)) faces.push(['top', [P(x, y, z + 1), P(x + 1, y, z + 1), P(x + 1, y + 1, z + 1), P(x, y + 1, z + 1)]]);
      return (
        <g key={i} strokeLinejoin="round" strokeWidth={0.7}>
          {faces.map(([f, poly]) => <polygon key={f} points={pts(poly)} fill={c[f]} stroke={seams ? 'var(--cube-seam)' : c[f]} />)}
        </g>
      );
    }
    const { x, y, z, w, h } = it;
    const sheet = [P(x, y, z), P(x + w, y, z), P(x + w, y + h, z), P(x, y + h, z)];
    const edge = [P(x, y + h, z), P(x + w, y + h, z), P(x + w, y + h, z - 0.06), P(x, y + h, z - 0.06)];
    const side = [P(x + w, y, z), P(x + w, y + h, z), P(x + w, y + h, z - 0.06), P(x + w, y, z - 0.06)];
    const row = (t, len, col, wdt) => (
      <polyline points={pts([P(x + w * 0.14, y + h * t, z), P(x + w * (0.14 + len * 0.72), y + h * t, z)])} stroke={col} strokeWidth={wdt} strokeLinecap="round" />
    );
    return (
      <g key={i}>
        <polygon points={pts(edge)} fill="var(--paper-edge)" />
        <polygon points={pts(side)} fill="var(--paper-side)" />
        <polygon points={pts(sheet)} fill="#FFFFFF" />
        {row(0.16, 0.45, 'var(--cube)', 2.2)}
        {row(0.36, 1, 'var(--paper-line)', 1.4)}
        {row(0.5, 0.85, 'var(--paper-line)', 1.4)}
        {row(0.64, 1, 'var(--paper-line)', 1.4)}
        {row(0.78, 0.55, 'var(--paper-line)', 1.4)}
      </g>
    );
  });

  // «frame»: caja fija para el encuadre, así un montón que crece no se reescala
  if (frame) {
    const [fx, fy, fz] = frame;
    [[0, 0, 0], [fx, 0, 0], [0, fy, 0], [fx, fy, 0], [0, 0, fz], [fx, 0, fz], [0, fy, fz], [fx, fy, fz]]
      .forEach(([a, b, c]) => grow(project(a, b, c, s)));
  }
  const pad = 2;
  const vb = `${(minX - pad).toFixed(1)} ${(minY - pad).toFixed(1)} ${(maxX - minX + pad * 2).toFixed(1)} ${(maxY - minY + pad * 2).toFixed(1)}`;
  return (
    <svg className={className} viewBox={vb} role={title ? 'img' : undefined} aria-label={title} aria-hidden={title ? undefined : true}>
      {ground}
      {shapes}
    </svg>
  );
}

/* ── Composiciones ─────────────────────────────────
   Una silueta clara por pieza y un solo acento (bloque claro o papel). */
const range = (a, b) => Array.from({ length: b - a + 1 }, (_, i) => a + i);
const box = (x0, x1, y0, y1, z0, z1, accent) => {
  const v = [];
  range(x0, x1).forEach((x) => range(y0, y1).forEach((y) => range(z0, z1).forEach((z) => v.push(accent ? [x, y, z, 1] : [x, y, z]))));
  return v;
};
const without = (list, drop) => list.filter(([x, y, z]) => !drop.some(([a, b, c]) => a === x && b === y && c === z));
const paper = (x, y, z, w = 1.4, h = 2) => ({ x, y, z, w, h });

export const ART = {
  // QX–01 Webs y plataformas: una pantalla con la barra superior clara
  web: {
    voxels: [
      ...box(0, 7, 0, 0, 0, 3),
      ...box(0, 7, 0, 0, 4, 4, true),
      ...box(3, 4, 1, 2, 0, 0),
    ],
  },
  // QX–02 Herramientas internas: la hoja de cálculo convertida en columnas
  tools: {
    papers: [paper(0, 0.3, 0, 1.8, 2.5)],
    voxels: [[1, 2, 3, 2], [2, 3, 4, 3], [1, 2, 2, 4]].flatMap((row, y) =>
      row.flatMap((h, x) => range(0, h - 1).map((z) => [x + 3, y, z]))),
  },
  // QX–03 Integraciones: dos sistemas y un bloque claro que cruza el puente
  integrations: {
    voxels: [...box(0, 1, 0, 1, 0, 3), ...box(6, 7, 0, 1, 0, 3), ...box(2, 5, 0, 1, 3, 3), [4, 0, 4, 1]],
  },
  // QX–04 Automatización: entran papeles, salen bloques
  automation: {
    papers: [paper(0.2, 0.3, 1, 1.1, 1.4), paper(1.5, 0.3, 1, 1.1, 1.4)],
    voxels: [
      ...box(0, 9, 0, 1, 0, 0),
      ...box(4, 4, 0, 1, 1, 2), ...box(6, 6, 0, 1, 1, 2), ...box(4, 6, 0, 1, 3, 3),
      [8, 0, 1, 1], [9, 0, 1, 1], [9, 1, 1, 1], [8, 1, 1, 1],
    ],
  },
  // QX–05 Asistentes: la pila de documentos y el bloque que los consulta
  assistant: {
    papers: [0, 1, 2, 3, 4].map((i) => paper(0, 0, i * 0.16, 2.2, 3)),
    voxels: [...box(3, 4, 0, 1, 0, 1), [3, 0, 2, 1]],
  },
  // QX–06 Mantenimiento: un bloque claro entra en su hueco
  maintenance: {
    voxels: [...without(box(0, 3, 0, 2, 0, 1), [[3, 2, 1]]), [3, 2, 2.4, 1]],
  },

  // Cómo trabajamos: del papel suelto al «=» completo
  step1: { papers: [paper(0, 0, 0, 1.6, 2.2), paper(2, 0.4, 0.4, 1.6, 2.2), paper(0.8, 2.6, 0.8, 1.6, 2.2)] },
  step2: { papers: [paper(0, 0, 0, 2, 2.8)], voxels: [[3, 1, 0, 1]] },
  step3: { voxels: [...box(0, 5, 0, 1, 0, 0), ...box(0, 2, 0, 1, 2, 2)] },
  step4: { voxels: [...box(0, 5, 0, 1, 0, 0), ...box(0, 5, 0, 1, 2, 2)] },

  // Para quién
  law: { papers: [0, 1, 2, 3].map((i) => paper(0, 0, i * 0.18, 2, 2.8)), voxels: [[3, 1, 0], [3, 1, 1, 1]] },
  marketing: { voxels: [...box(0, 0, 0, 0, 0, 0), ...box(1, 1, 0, 0, 0, 1), ...box(2, 2, 0, 0, 0, 2), ...box(3, 3, 0, 0, 0, 3, true)] },
  health: { voxels: [...box(2, 2, 0, 0, 0, 4), ...box(0, 1, 0, 0, 2, 2), ...box(3, 4, 0, 0, 2, 2)] },

  // Contacto
  contact: {
    papers: [paper(0, 0, 0, 1.6, 2.2)],
    voxels: [[3, 0, 0], [4, 0, 0], [3, 1, 0], [4, 1, 0], [4, 0, 1, 1]],
  },
};

/* Montón de bloques para la cuenta: n cubos en capas de 8 × 4 */
export function pile(n) {
  const v = [];
  for (let i = 0; i < n; i++) v.push([i % 8, Math.floor(i / 8) % 4, Math.floor(i / 32)]);
  return v;
}
